import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n';

export interface CatchShoutCatchWhispererProps {
  themeTitle: string;
  targetEmoji?: string;
  targetChanged?: boolean;
  roundCatches?: { A: number; B: number };
  teamId: 'A' | 'B';
}

export const CatchShoutCatchWhisperer: React.FC<CatchShoutCatchWhispererProps> = ({
  themeTitle,
  targetEmoji,
  targetChanged = false,
  roundCatches,
  teamId,
}) => {
  const { t } = useTranslation();
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (targetChanged && targetEmoji) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 800);
      return () => clearTimeout(timer);
    }
  }, [targetChanged, targetEmoji]);

  const teamRoundCatches = roundCatches?.[teamId] ?? 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '1 1 0',
        minHeight: 0,
        padding: '1.5rem',
        textAlign: 'center',
        gap: '1.5rem',
      }}
    >
      <div
        style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.35rem)',
          fontWeight: 700,
          color: 'var(--text-primary)',
        }}
      >
        {themeTitle}
      </div>

      <div
        style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--color-primary)',
          background: 'rgba(var(--color-primary-rgb), 0.12)',
          borderRadius: 999,
          padding: '0.4rem 1rem',
        }}
      >
        {t('gameplay.catchShoutCatch.roleWhisperer')}
      </div>

      <div
        style={{
          fontSize: '1.05rem',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          maxWidth: 320,
        }}
      >
        {t('gameplay.catchShoutCatch.whispererHint')}
      </div>

      <div
        className={pulse ? 'catch-shout-catch__target--pulse' : undefined}
        style={{
          fontSize: 'clamp(5rem, 18vw, 8rem)',
          lineHeight: 1,
          padding: '1.5rem 2rem',
          background: 'rgba(245, 158, 11, 0.15)',
          border: '4px solid rgba(245, 158, 11, 0.45)',
          borderRadius: 24,
          minWidth: 140,
          minHeight: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {targetEmoji ?? '❓'}
      </div>

      <div
        style={{
          fontSize: '1.2rem',
          fontWeight: 800,
          color: '#B45309',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        {t('gameplay.catchShoutCatch.shoutIt')}
      </div>

      <div
        style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#047857',
          background: 'rgba(52, 211, 153, 0.16)',
          border: '1.5px solid rgba(52, 211, 153, 0.35)',
          borderRadius: 999,
          padding: '0.45rem 0.85rem',
        }}
      >
        {t('gameplay.catchShoutCatch.teamRoundCatches', { count: teamRoundCatches })}
      </div>
    </div>
  );
};
